import { Alert, Button, Divider, Group, Select, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import type { AdPlatform } from '@marketing-tool/shared';
import { Line, Meta } from '@marketing-tool/shared';

import { plural, preventEnterSubmit } from '../../../lib/utils';

export type BulkEditFormData = {
    configuredStatus?: string;
};

type InternalFormData = {
    configuredStatus: string;
};

type BulkEditModalProps = {
    platform: AdPlatform;
    entityType: 'campaign' | 'adGroup' | 'ad';
    selectedCount: number;
    onSubmit: (data: BulkEditFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
};

export const BulkEditModal = ({
    platform,
    entityType,
    selectedCount,
    onSubmit,
    onCancel,
    isSubmitting,
}: BulkEditModalProps) => {
    const form = useForm<InternalFormData>({
        initialValues: {
            configuredStatus: '',
        },
    });

    const handleSubmit = form.onSubmit((values) => {
        // Strip empty string sentinels
        const updates: BulkEditFormData = {};
        if (values.configuredStatus) {
            updates.configuredStatus = values.configuredStatus;
        }

        // Skip submit if no changes were made
        if (Object.keys(updates).length === 0) {
            return;
        }

        onSubmit(updates);
    });

    const statusOptions =
        platform === 'line'
            ? [
                  { value: '', label: '-- No Change --' },
                  { value: Line.ConfiguredStatus.Active, label: 'Active' },
                  { value: Line.ConfiguredStatus.Paused, label: 'Paused' },
                  { value: Line.ConfiguredStatus.Draft, label: 'Draft' },
              ]
            : [
                  { value: '', label: '-- No Change --' },
                  { value: Meta.ConfiguredStatus.Active, label: 'Active' },
                  { value: Meta.ConfiguredStatus.Paused, label: 'Paused' },
                  { value: Meta.ConfiguredStatus.Archived, label: 'Archived' },
              ];

    const entityLabel =
        entityType === 'adGroup' ? (['ad group', 'ad groups'] as const) : ([entityType, `${entityType}s`] as const);

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <Alert color="blue">
                    You are editing <strong>{selectedCount}</strong>{' '}
                    {plural(selectedCount, entityLabel[0], entityLabel[1])}. Only fields you change will be updated.
                </Alert>

                <Select
                    label="Status"
                    value={form.values.configuredStatus}
                    onChange={(val) => form.setFieldValue('configuredStatus', val ?? '')}
                    disabled={isSubmitting}
                    data={statusOptions}
                    description='Leave as "No Change" to keep existing values'
                />

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="subtle" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting || !form.values.configuredStatus}>
                        {isSubmitting
                            ? 'Updating...'
                            : `Update ${selectedCount} ${plural(selectedCount, entityLabel[0], entityLabel[1])}`}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
